// ADU cost calculator — pricing logic & question config
// Mirrors backend /api/adu-leads logic for instant preview.

export const ADU_PRICING = {
  basePerSqft: {
    "Garage Conversion": 225,
    "Junior ADU": 250,
    "Attached ADU": 290,
    "Detached ADU": 340,
    "Not Sure": 310,
  },
  finishMultiplier: {
    "Basic / Rental-Ready": 0,
    Standard: 0.08,
    Premium: 0.18,
  },
  bathrooms: {
    "1 Bathroom": 8000,
    "2 Bathrooms": 18000,
  },
  kitchen: {
    Kitchenette: 6000,
    "Full Kitchen": 12000,
    "Premium Kitchen": 22000,
  },
  foundation: {
    "Existing garage/slab": 3000,
    "New slab foundation": 20000,
    "Raised foundation": 30000,
    "Not sure": 18000,
  },
  utilities: {
    "Plumbing connection": 12000,
    "Electrical upgrade": 8000,
    "Sewer connection": 10000,
    "Gas line": 3500,
    "HVAC system": 7000,
    "Water heater": 3000,
  },
  site: {
    "Easy access": 0,
    "Moderate access": 5000,
    "Difficult access": 12000,
    "Slope or grading needed": 25000,
    "Not sure": 8000,
  },
  permitsFlat: 12000,
};

export const SIZE_PRESETS = [300, 400, 500, 600, 750, 1000];

export const ADU_QUESTIONS = [
  {
    key: "adu_type",
    title: "What type of ADU are you planning?",
    type: "single",
    options: ["Garage Conversion", "Attached ADU", "Detached ADU", "Junior ADU", "Not Sure"],
  },
  {
    key: "size_sqft",
    title: "Approximate ADU size?",
    type: "size",
    options: SIZE_PRESETS,
  },
  {
    key: "finish_level",
    title: "Finish level?",
    type: "single",
    options: ["Basic / Rental-Ready", "Standard", "Premium"],
    helper: "Basic = rental-ready • Standard = upgraded finishes • Premium = high-end design.",
  },
  {
    key: "bathrooms",
    title: "How many bathrooms?",
    type: "single",
    options: ["1 Bathroom", "2 Bathrooms"],
  },
  {
    key: "kitchen_type",
    title: "Kitchen type?",
    type: "single",
    options: ["Kitchenette", "Full Kitchen", "Premium Kitchen"],
  },
  {
    key: "foundation",
    title: "Foundation condition?",
    type: "single",
    options: ["Existing garage/slab", "New slab foundation", "Raised foundation", "Not sure"],
  },
  {
    key: "utilities",
    title: "Utilities needed?",
    type: "multi",
    options: [
      "Plumbing connection",
      "Electrical upgrade",
      "Sewer connection",
      "Gas line",
      "HVAC system",
      "Water heater",
    ],
    helper: "Select all that apply.",
  },
  {
    key: "site_condition",
    title: "Site condition?",
    type: "single",
    options: ["Easy access", "Moderate access", "Difficult access", "Slope or grading needed", "Not sure"],
  },
];

const roundTo500 = (n) => Math.round(n / 500) * 500;

export function computeAduEstimate(answers) {
  const p = ADU_PRICING;
  const sqft = Number(answers.size_sqft) || 0;
  const baseRate = p.basePerSqft[answers.adu_type] ?? p.basePerSqft["Not Sure"];
  const base = baseRate * sqft;
  const finishMult = p.finishMultiplier[answers.finish_level] ?? 0;
  const finishAdd = base * finishMult;
  const bathAdd = p.bathrooms[answers.bathrooms] ?? 0;
  const kitchenAdd = p.kitchen[answers.kitchen_type] ?? 0;
  const foundationAdd = p.foundation[answers.foundation] ?? 0;
  const siteAdd = p.site[answers.site_condition] ?? 0;
  const utilitiesAdd = (answers.utilities || []).reduce(
    (sum, u) => sum + (p.utilities[u] ?? 0),
    0
  );
  const permits = p.permitsFlat;
  const total = base + finishAdd + bathAdd + kitchenAdd + foundationAdd + utilitiesAdd + siteAdd + permits;

  return {
    base_cost: roundTo500(base),
    finish_adjustment: roundTo500(finishAdd),
    bathrooms_cost: bathAdd,
    kitchen_cost: kitchenAdd,
    foundation_cost: foundationAdd,
    utilities_cost: utilitiesAdd,
    site_cost: siteAdd,
    permits_cost: permits,
    calculated_total: roundTo500(total),
    reasonable_estimate: roundTo500(total * 0.9),
    expected_low: roundTo500(total),
    expected_high: roundTo500(total * 1.15),
    high_end_estimate: roundTo500(total * 1.25),
  };
}

export const formatUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export function isAnswerComplete(question, answers) {
  const val = answers[question.key];
  if (question.type === "multi") return true; // optional
  if (question.type === "size") return val && Number(val) >= 100;
  return Boolean(val);
}
